/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */

import { Inject, Injectable, InjectionToken, Optional } from '@angular/core'
import { ApiServiceBase, IApiConfig } from '@sightline/models-misc'
import { HTTPMethod } from 'http-method-enum'
import { IChangeUserAccountStateRequest } from '../../change-user-account-state.model'
import { AppUserClaims, OrgPermissionDto } from '../../claims.model'
import { AddUserToOktaGroupRequestDto, EnrollMfaRequestDto, FactorEnrolledResponseDto, ActivateMfaRequestDto, UserFactorDto, UserPreferenceKey, UserPreferenceDto, VerifyMfaCodeDto } from '../../mfa.model'
import { IMyProfile, IEditMyProfileResponse } from '../../my-profile.model'
import { IUserInvitationRequest, IUserResendInvitationRequest } from '../../user-invitation.model'
import { UserPermissionsDto } from '../../user-permissions.model'
import { IUser, UserDto, UserDetailsExtendedDto } from '../../user.model'
import { TokenType, ValidateTokenResponse } from '../../validate-token'

export const API_CONFIG = new InjectionToken<IApiConfig>('ApiConfig')

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  constructor(private serviceBase: ApiServiceBase, @Inject(API_CONFIG) @Optional() private config?: IApiConfig) { }

  public async validateToken(tokenValue: string, tokenType: TokenType): Promise<ValidateTokenResponse> {
    return this.serviceBase
      .callUnprotectedEndpoint<ValidateTokenResponse>(
        HTTPMethod.GET,
        `users/validation-tokens/${tokenValue}/${tokenType}`,
        this.config
      )
      .toPromise()
  }

  public async registerUser(user: IUser): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST, 'users/rpc-register', this.config, user)
      .toPromise()
  }

  public async inviteUser(user: IUserInvitationRequest): Promise<number> {
    return this.serviceBase
      .callProtectedEndpoint<number>(HTTPMethod.POST, 'users', this.config, user)
      .toPromise()
  }

  public async forgotPassword(username: string): Promise<boolean> {
    return this.serviceBase
      .callUnprotectedEndpoint<boolean>(HTTPMethod.POST, 'users/rpc-forgotPassword', this.config, {
        username,
      })
      .toPromise()
  }

  public async forgotUsername(email: string): Promise<boolean> {
    return this.serviceBase
      .callUnprotectedEndpoint<boolean>(HTTPMethod.POST, 'users/rpc-forgotUsername', this.config, {
        email,
      })
      .toPromise()
  }

  public async isUsernameAvailable(username: string, registrationToken: string): Promise<boolean> {
    return this.serviceBase
      .callUnprotectedEndpoint<boolean>(HTTPMethod.POST, 'users/rpc-isUsernameAvailable', this.config, {
        username,
        registrationToken,
      })
      .toPromise()
  }

  public async resetPassword(forgotPasswordToken: string, newPassword: string): Promise<boolean> {
    return this.serviceBase
      .callUnprotectedEndpoint<boolean>(HTTPMethod.POST, 'users/rpc-resetPassword', this.config, {
        forgotPasswordToken,
        newPassword,
      })
      .toPromise()
  }

  public async resetUserPassword(newPassword: string): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST, 'users/rpc-resetUserPassword', this.config, {
        newPassword,
      })
      .toPromise()
  }

  public async adminResetPassword(userId: number): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST,
        'users/rpc-AdminResetPassword',
        this.config, {
        userRId: userId,
      })
      .toPromise()
  }

  public async revokeInvitation(userId: number, note: string): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST, 'users/rpc-revokeInvitation', this.config, {
        userId,
        note,
      })
      .toPromise()
  }

  public async resendInvitation(userId: number, resendInvitationDto: IUserResendInvitationRequest): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(
        HTTPMethod.POST,
        `users/${userId}/rpc-resendInvitation`,
        this.config,
        resendInvitationDto
      )
      .toPromise()
  }

  public async changeUserState(userId: number, changeStateDto: IChangeUserAccountStateRequest): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.PUT,
        `users/${userId}/change-state`,
        this.config,
        changeStateDto
      )
      .toPromise()
  }

  public async reopenUser(userId: number, reopenDto: IChangeUserAccountStateRequest): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.PUT,
        `users/${userId}/rpc-Reopen`,
        this.config,
        reopenDto
      )
      .toPromise()
  }

  public getUserClaims(): Promise<AppUserClaims[]> {
    return this.serviceBase
      .callProtectedEndpoint<AppUserClaims[]>(HTTPMethod.GET, 'users/claims', this.config)
      .toPromise()
  }

  public getUserInfo(): Promise<UserDto> {
    return this.serviceBase
      .callProtectedEndpoint<UserDto>(HTTPMethod.GET, 'users/rpc-getUserInfo', this.config)
      .toPromise()
  }

  public getUserDetails(userId: number): Promise<UserDetailsExtendedDto> {
    return this.serviceBase
      .callProtectedEndpoint<any>(HTTPMethod.GET, `users/${userId}/details`, this.config)
      .toPromise()
  }

  public getUserPermissions(userId: number): Promise<UserPermissionsDto> {
    return this.serviceBase
      .callProtectedEndpoint<UserPermissionsDto>(
        HTTPMethod.GET,
        `users/${userId}/permissions`,
        this.config
      )
      .toPromise()
  }

  public getMyProfile(): Promise<IMyProfile> {
    return this.serviceBase
      .callProtectedEndpoint<any>(HTTPMethod.GET, 'users', this.config)
      .toPromise()
  }

  public editMyProfile(payload: IMyProfile): Promise<IEditMyProfileResponse> {
    return this.serviceBase
      .callProtectedEndpoint<IEditMyProfileResponse>(HTTPMethod.PATCH, 'users', this.config, payload)
      .toPromise()
  }

  public getEmailDomainRestrictions(): Promise<any> {
    return this.serviceBase
      .callProtectedEndpoint<any>(HTTPMethod.GET, 'users/email-domain-restrictions', this.config)
      .toPromise()
  }

  public getPermissionsByOrgId(): Promise<OrgPermissionDto> {
    return this.serviceBase.callProtectedEndpoint<any>(
      HTTPMethod.GET,
      'users/permissions',
      this.config
    ).toPromise()
  }

  //mfa methods
  public postAddUserToOktaGroup(addUserToOktaGroupRequestDto: AddUserToOktaGroupRequestDto): Promise<void> {
    return this.serviceBase
      .callProtectedEndpoint<void>(HTTPMethod.POST, 'users/add-okta-group', this.config, addUserToOktaGroupRequestDto)
      .toPromise()
  }

  public postMfaEnroll(enrollRequestDto: EnrollMfaRequestDto): Promise<FactorEnrolledResponseDto> {
    return this.serviceBase
      .callProtectedEndpoint<FactorEnrolledResponseDto>(HTTPMethod.POST, 'users/mfa-enroll', this.config, enrollRequestDto)
      .toPromise()
  }

  public postMfaActivate(activateRequestDto: ActivateMfaRequestDto): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST, 'users/mfa-activate', this.config, activateRequestDto)
      .toPromise()
  }

  public getMfaFactors(): Promise<Array<UserFactorDto>> {
    return this.serviceBase
      .callProtectedEndpoint<Array<UserFactorDto>>(HTTPMethod.GET, 'users/mfa-factors', this.config)
      .toPromise()
  }
  //end mfa methods

  public getUserPreferences(keys: Array<UserPreferenceKey>): Promise<Array<UserPreferenceDto>> {
    let query = ''
    for (let i = 0; i < keys.length; i++) {
      query += `keys=${keys[i]}`
    }

    return this.serviceBase
      .callProtectedEndpoint<Array<UserPreferenceDto>>(HTTPMethod.GET, `users/user-preferences?${query}`, this.config)
      .toPromise()
  }

  public postUserPreferences(userPreferences: Array<UserPreferenceDto>): Promise<void> {
    return this.serviceBase
      .callProtectedEndpoint<void>(HTTPMethod.POST, 'users/user-preferences', this.config, userPreferences)
      .toPromise()
  }

  public getUserMfaPreference = (username: string): Promise<UserPreferenceDto> =>
    this.serviceBase.callUnprotectedEndpoint<UserPreferenceDto>(HTTPMethod.GET, `users/mfa-preference?username=${encodeURIComponent(username)}`, this.config)
      .toPromise()

  public postSendMfaCode(): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST, 'users/mfa-code', this.config)
      .toPromise()
  }

  public postVerifyMfaCode(verifyMfaCodeDto: VerifyMfaCodeDto): Promise<boolean> {
    return this.serviceBase
      .callProtectedEndpoint<boolean>(HTTPMethod.POST, 'users/mfa-code/verify', this.config, verifyMfaCodeDto)
      .toPromise()
  }
}
